import Guid from "_shared/guids/guid";
import type { Resource } from "permissions/resources/permission-resource";
import AHDate from "_shared/dates/date";
import PermissionTemplateSchema, {
  PermissionTemplateJSON,
} from "permissions/templates/_schemas/permission-template-schema";
import { PermissionTemplateCtorParams } from "permissions/_views/permission-table-view-model";
import { resourceFromJSON } from "permissions/resources/permission-resource-factory";

export default class PermissionTemplate {
  public static fromJSON(templateJSON: PermissionTemplateJSON): PermissionTemplate {
    const parsed = PermissionTemplateSchema.deserialize(templateJSON);

    // A template grants a resource or it does not; holding the same one twice says nothing
    // extra and would apply it twice. Templates saved before the editor stopped seeding
    // duplicates still carry them, so they are dropped on the way in rather than left for
    // every reader to cope with.
    const resources: Resource[] = [];
    const seenResources = new Set<string>();
    for (const resourceJSON of templateJSON.resources) {
      const resource = resourceFromJSON(resourceJSON);
      if (!resource) continue;

      const key = JSON.stringify(resource.toJSON());
      if (seenResources.has(key)) continue;

      seenResources.add(key);
      resources.push(resource);
    }

    const ctorParams: PermissionTemplateCtorParams = {
      ...parsed,
      resources,
    };

    return new PermissionTemplate(ctorParams);
  }

  public static fromJSONArray(jsonArray: PermissionTemplateJSON[]): PermissionTemplate[] {
    return jsonArray.map((j) => PermissionTemplate.fromJSON(j));
  }

  public readonly id: Guid;
  public readonly userId: Guid;
  public readonly resources: Resource[];
  public readonly name: string;
  public readonly description?: string;
  public readonly createdDate?: AHDate;
  public readonly deletedDate?: AHDate;
  public readonly version: number;

  public get isDeleted(): boolean {
    return !!this.deletedDate;
  }

  public constructor(params: PermissionTemplateCtorParams) {
    this.id = params.id;
    this.userId = params.userId;
    this.resources = params.resources;
    this.name = params.name;
    this.description = params.description;
    this.createdDate = params.createdDate;
    this.deletedDate = params.deletedDate;
    this.version = params.version ?? 0;
  }

  public toJSON(): PermissionTemplateJSON {
    const params = {
      id: this.id,
      userId: this.userId,
      resources: this.resources.map((res) => res.toJSON ? res.toJSON() : res),
      name: this.name,
      description: this.description,
      createdDate: this.createdDate,
      deletedDate: this.deletedDate,
      version: this.version,
    };
    return PermissionTemplateSchema.serialize(params);
  }

  public isEqualTo(other: PermissionTemplate): boolean {
    if (!other) return false;
    const idsEqual = Boolean((!this.id && !other.id) || (this.id && other.id && this.id.isEqualTo(other.id)));
    return (
      idsEqual &&
      this.userId.isEqualTo(other.userId) &&
      this.name === other.name &&
      this.description === other.description &&
      this.resources.length === other.resources.length &&
      this.resources.every((res) => other.resources.some((otherRes) => res.isEqualTo(otherRes)))
    );
  }

  public toString(): string {
    return `PermissionTemplate { 
      id: ${this.id.value}, 
      userId: ${this.userId.value}, 
      name: ${this.name}, 
      description: ${this.description ?? "undefined"} 
      createdDate: ${this.createdDate?.toString() ?? "undefined"},
      deletedDate: ${this.deletedDate?.toString() ?? "undefined"},
      resources: [${this.resources.map((res) => res.toString()).join(", ")}]
    }`;
  }
}
